import {
  DateFieldProps,
  DynamicField,
  DynamicForm,
  FieldVariant,
  SelectFieldProps,
  SelectOption,
  TextareaFieldProps,
} from '@/components/form-builder/form-builder.types';
import { DacDocSubmissionRepository } from '@/store/dac-doc-submission/dac-doc-submission-state.repository';
import { DocSubmission, WithMetadata } from '@/types';
import { combineLatest, map, Observable, of } from 'rxjs';

interface getDacSubmissionPiecesFormObjectProps {
  store: DacDocSubmissionRepository;
  className?: string;
  rawDocOptions?: Observable<WithMetadata<DocSubmission>[]>;
  docOptions?: Observable<SelectOption[]>;
  loadDocOptions?: (docTypeId: number) => void;
  isDisabled?: Observable<boolean>;
  usedDocSubmissionIds?: Observable<number[]>;
}

export const getDacSubmissionPiecesFormObject = ({
  store,
  className,
  rawDocOptions,
  docOptions,
  isDisabled,
  usedDocSubmissionIds,
}: getDacSubmissionPiecesFormObjectProps): DynamicForm => {
  // Filter docOptions to exclude already used submission piece IDs
  // but keep the current selection even if it's used elsewhere
  const filteredDocOptions = combineLatest([
    docOptions || of([]),
    usedDocSubmissionIds || of([]),
    store.getObservable<SelectOption>('docSubmissionNotInDacSelected') || of(null),
  ]).pipe(
    map(([options, usedIds, currentSelection]) => {
      const usedIdSet = new Set(usedIds);
      return options.filter(option => {
        // Always keep the current selection
        if (currentSelection && currentSelection.code === option.code) {
          return true;
        }
        // Exclude options that are used elsewhere
        return !usedIdSet.has(option.code as number);
      });
    })
  );

  const isDateRequired = combineLatest([rawDocOptions!, store.getNestedObservable<number>('dto', 'docSoumissionId')!]).pipe(
    map(([options, docSoumissionId]) => {
      const selected = options.find(doc => doc.id === docSoumissionId);
      return !selected || !selected.validationDateRequired;
    })
  );

  const nameField: DynamicField<SelectFieldProps> = {
    label: 'dac.docSubmission.form.name.label',
    variant: FieldVariant.SELECT,
    isRequired: true,
    isDisabled,
    error: store.getNestedObservable<string>('errors', 'docSoumissionId'),
    props: {
      value: store.getObservable('docSubmissionNotInDacSelected'),
      placeholder: 'dac.docSubmission.form.name.placeholder',
      options: filteredDocOptions,
      onSelectChange: (value: SelectOption) => {
        store.set('docSubmissionNotInDacSelected', value);
        store.setNested('dto', 'docSoumissionId', value.code);
        store.setNested('dto', 'dateValidate', new Date());
        store.clearError('docSoumissionId');
      },
    },
  };

  const deadlineField: DynamicField<DateFieldProps> = {
    label: 'dac.docSubmission.form.deadline.label',
    variant: FieldVariant.DATE,
    error: store.getNestedObservable<string>('errors', 'dateValidate'),
    isRequired: true,
    isHidden: isDateRequired,
    isDisabled,
    props: {
      view: 'date',
      value: store.getNestedObservable<Date>('dto', 'dateValidate'),
      onChange: (value: Date) => {
        store.setNested('dto', 'dateValidate', value);
        store.clearError('dateValidate');
      },
    },
  };

  const commentField: DynamicField<TextareaFieldProps> = {
    label: 'dac.docSubmission.form.comment.label',
    variant: FieldVariant.TEXTAREA,
    isDisabled,
    props: {
      value: store.getNestedObservable<string>('dto', 'commentaire'),
      placeholder: 'dac.docSubmission.form.comment.placeholder',
      onChange: (value: string) => {
        store.setNested('dto', 'commentaire', value);
      },
    },
  };

  return {
    title: 'dac.docSubmission.form',
    description: '',
    isHeaderHidden: true,
    class: className,
    grids: [
      {
        title: '',
        isHeaderHidden: true,
        gridItems: [
          {
            fields: [nameField, deadlineField],
            ignoreHiddenFields: of(true),
          },
          {
            fields: [commentField],
          },
        ],
      },
    ],
  };
};
